/**
 * Reusable "quick-add" dialogs for Company and Contact, extracted from the
 * Deal form so any page (Deals, Contacts, Companies) can offer inline
 * creation without duplicating the mini-form logic.
 *
 * Contract:
 * - Both dialogs call `onCreated(newRow)` with the freshly-inserted row so
 *   the caller can (a) auto-select it in its own form / list and
 *   (b) invalidate its own react-query cache.
 * - `QuickAddContactDialog` accepts `defaultCompanyId` and, when set,
 *   *locks* the company field so callers who open the dialog from a
 *   company-scoped context (e.g. Company profile) can't accidentally
 *   attach the contact to a different company.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export function QuickAddCompanyDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [website, setWebsite] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(""); setIndustry(""); setWebsite(""); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/companies", { name, industry, website });
      toast.success("Company added");
      onCreated(data);
      reset();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Quick-add company</DialogTitle>
          <DialogDescription className="sr-only">
            Create a new company inline without leaving the current form.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input data-testid="quick-company-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Industry</Label>
              <Input data-testid="quick-company-industry" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="e.g. K-12 Education" />
            </div>
            <div className="space-y-1.5">
              <Label>Website</Label>
              <Input data-testid="quick-company-website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="example.com" />
            </div>
          </div>
          <div className="text-xs text-muted-foreground">You can add more details later from the Companies page.</div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving || !name.trim()}
              data-testid="quick-company-save"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {saving ? "Adding…" : "Add & select"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * When `lockCompany` is true, the company select is disabled — used from the
 * Company profile view where the parent context dictates the company.
 * When `defaultCompanyId` is set but `lockCompany` is false, the field is
 * still editable (deal form use-case: user may want to add a contact for a
 * different company than the one currently selected).
 */
export function QuickAddContactDialog({
  open, onOpenChange, companies, defaultCompanyId, onCreated, lockCompany = false,
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [title, setTitle] = useState("");
  const [companyId, setCompanyId] = useState(defaultCompanyId || "");
  const [saving, setSaving] = useState(false);

  // Sync inbound default when the parent's selection changes while dialog
  // is closed. useEffect (not useMemo) — this is a side-effect, not a memo.
  useEffect(() => { setCompanyId(defaultCompanyId || ""); }, [defaultCompanyId]);

  const reset = () => {
    setName(""); setEmail(""); setMobile(""); setTitle("");
    setCompanyId(defaultCompanyId || "");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!companyId) {
      toast.error("Company is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/contacts", {
        name,
        email: email || null,
        mobile: mobile || null,
        title,
        company_id: companyId,
      });
      toast.success("Contact added");
      onCreated(data);
      reset();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Quick-add contact</DialogTitle>
          <DialogDescription className="sr-only">
            Create a new contact inline. Contacts must belong to a company.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input data-testid="quick-contact-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="quick-contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile</Label>
              <Input data-testid="quick-contact-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+91 …" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input data-testid="quick-contact-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Company <span className="text-destructive">*</span></Label>
              <select
                data-testid="quick-contact-company"
                className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full disabled:opacity-70 disabled:cursor-not-allowed"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                disabled={lockCompany}
                required
              >
                <option value="">— Select company —</option>
                {(companies || []).map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving || !name.trim() || !companyId}
              data-testid="quick-contact-save"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {saving ? "Adding…" : "Add & select"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
